import React, { useEffect, useMemo, useState } from "react";
import StarIcon from "@mui/icons-material/Star";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";

/** Matches Android `INTERMISSION` theme. */
const I = {
    cardBg: "linear-gradient(180deg, rgba(232,250,245,0.96) 0%, rgba(210,242,233,0.94) 50%, rgba(186,230,220,0.92) 100%)",
    cardBorder: "rgba(212, 175, 55, 0.55)",
    cardGlow: "0 14px 28px rgba(45, 180, 160, 0.28)",
    filigree: "rgba(184, 148, 58, 0.22)",
    titleGold: "#9a7209",
    titleTeal: "#0f5c54",
    bodyMuted: "rgba(15, 61, 54, 0.62)",
    marble: "linear-gradient(135deg, #f7f5f0 0%, #ebe4d8 50%, #f5f1ea 100%)",
    marbleBorder: "rgba(148, 130, 100, 0.35)",
    marbleHighlight: "linear-gradient(135deg, #fff8e7 0%, #f3e6c0 50%, #e8d5a0 100%)",
    marbleHighlightBorder: "rgba(184, 134, 11, 0.45)",
    button: "linear-gradient(135deg, #5ec4b8 0%, #2a9d8f 50%, #1a7a72 100%)",
    buttonRim: "#d4af37",
    buttonText: "#f8fffc",
    serif: 'Georgia, "Palatino Linotype", "Book Antiqua", Palatino, "Times New Roman", serif',
};

const COMPLIMENTS = [
    "Good job!",
    "Nice work!",
    "Well done!",
    "Awesome!",
    "Brilliant!",
    "You nailed it!",
    "Great solve!",
    "Fantastic!",
    "Impressive!",
    "Way to go!",
];

function pickCompliment() {
    return COMPLIMENTS[Math.floor(Math.random() * COMPLIMENTS.length)];
}

function GoldFiligree() {
    const stroke = I.filigree;
    return (
        <Box
            aria-hidden
            component="svg"
            viewBox="0 0 320 420"
            preserveAspectRatio="xMidYMid slice"
            sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                opacity: 0.95,
            }}
        >
            <path
                d="M28 48 C48 28, 78 34, 92 52 C78 48, 58 58, 48 78 C42 58, 34 52, 28 48Z"
                fill={stroke}
            />
            <path
                d="M292 48 C272 28, 242 34, 228 52 C242 48, 262 58, 272 78 C278 58, 286 52, 292 48Z"
                fill={stroke}
            />
            <path
                d="M28 372 C48 392, 78 386, 92 368 C78 372, 58 362, 48 342 C42 362, 34 368, 28 372Z"
                fill={stroke}
            />
            <path
                d="M292 372 C272 392, 242 386, 228 368 C242 372, 262 362, 272 342 C278 362, 286 368, 292 372Z"
                fill={stroke}
            />
            <circle cx="160" cy="36" r="2.2" fill={stroke} />
            <circle cx="160" cy="384" r="2.2" fill={stroke} />
            <path d="M110 36 Q160 18 210 36" stroke={stroke} strokeWidth="1.2" fill="none" />
            <path d="M110 384 Q160 402 210 384" stroke={stroke} strokeWidth="1.2" fill="none" />
        </Box>
    );
}

function PulsingStarBadge() {
    return (
        <Box
            sx={{
                width: 96,
                height: 96,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.25,
                position: "relative",
                "@keyframes wwStarGlow": {
                    "0%, 100%": { opacity: 0.45, transform: "scale(0.9)" },
                    "50%": { opacity: 1, transform: "scale(1.15)" },
                },
                "@keyframes wwStarPulse": {
                    "0%, 100%": { transform: "scale(1)" },
                    "50%": { transform: "scale(1.06)" },
                },
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    bgcolor: "rgba(252, 211, 77, 0.45)",
                    animation: "wwStarGlow 2.8s ease-in-out infinite",
                }}
            />
            <Box
                sx={{
                    width: 76,
                    height: 76,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(145deg, #f6e27a 0%, #d4af37 50%, #a67c1a 100%)",
                    border: "2px solid rgba(255, 236, 179, 0.85)",
                    boxShadow: "0 4px 12px rgba(212, 175, 55, 0.55)",
                    animation: "wwStarPulse 2.8s ease-in-out infinite",
                    zIndex: 1,
                }}
            >
                <Box
                    sx={{
                        width: 62,
                        height: 62,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(120, 80, 10, 0.22)",
                    }}
                >
                    <StarIcon sx={{ fontSize: 34, color: "#fff8dc", filter: "drop-shadow(0 0 2px #ffe08a)" }} />
                </Box>
            </Box>
        </Box>
    );
}

function MarbleStat({ label, value, highlight }) {
    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                borderRadius: 999,
                px: 1.25,
                py: 1.5,
                minHeight: 72,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                background: highlight ? I.marbleHighlight : I.marble,
                border: `1.2px solid ${highlight ? I.marbleHighlightBorder : I.marbleBorder}`,
                boxSizing: "border-box",
            }}
        >
            <Typography
                sx={{
                    fontFamily: I.serif,
                    fontSize: 11,
                    color: I.bodyMuted,
                    mb: 0.5,
                    letterSpacing: 0.4,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontFamily: I.serif,
                    fontSize: 17,
                    fontWeight: 700,
                    color: highlight ? "#8b6914" : I.titleTeal,
                    textAlign: "center",
                    lineHeight: 1.2,
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

/**
 * Android-parity level-complete interstitial (WordMaster + IntermissionCardShell).
 * Used by 1080p recording capture.
 */
export default function WordWheelIntermissionCompleteDialog({
    open,
    onClose,
    durationLabel,
    scoreCoins = 0,
    hintCoinsSpent = 0,
    contained = false,
}) {
    const [title, setTitle] = useState(COMPLIMENTS[0]);

    useEffect(() => {
        if (open) setTitle(pickCompliment());
    }, [open]);

    const scoreLabel = useMemo(() => {
        const n = Number(scoreCoins);
        return n > 0 ? `+${n}` : "—";
    }, [scoreCoins]);

    const hasHints = Number(hintCoinsSpent) > 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            disablePortal={contained}
            disableScrollLock={contained}
            slotProps={{
                backdrop: {
                    sx: {
                        bgcolor: "rgba(4, 28, 34, 0.55)",
                        ...(contained ? { position: "absolute" } : {}),
                    },
                },
            }}
            sx={
                contained
                    ? {
                          position: "absolute",
                          inset: 0,
                          zIndex: 30,
                          "& .MuiDialog-container": {
                              position: "absolute",
                              inset: 0,
                              alignItems: "center",
                              px: 1.5,
                          },
                      }
                    : {
                          "& .MuiDialog-container": {
                              px: 2,
                          },
                      }
            }
            PaperProps={{
                sx: {
                    m: 0,
                    width: "100%",
                    maxWidth: 360,
                    borderRadius: "28px",
                    background: I.cardBg,
                    border: `1.5px solid ${I.cardBorder}`,
                    boxShadow: I.cardGlow,
                    overflow: "hidden",
                    position: "relative",
                    px: 2.75,
                    pt: 3.5,
                    pb: 2.75,
                },
            }}
        >
            <GoldFiligree />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <PulsingStarBadge />
                <Typography
                    sx={{
                        fontFamily: I.serif,
                        fontSize: 28,
                        fontWeight: 700,
                        color: I.titleGold,
                        textAlign: "center",
                        mb: 1.25,
                        letterSpacing: 0.3,
                        lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: 1.25,
                        width: "100%",
                        mb: hasHints ? 1.5 : 0,
                    }}
                >
                    <MarbleStat label="Time" value={durationLabel || "—"} />
                    <MarbleStat label="Score" value={scoreLabel} highlight />
                </Box>
                {hasHints ? (
                    <Typography
                        sx={{
                            fontFamily: I.serif,
                            fontSize: 13,
                            color: I.bodyMuted,
                            textAlign: "center",
                            mb: 0.5,
                        }}
                    >
                        {`Hints used: −${hintCoinsSpent} coins`}
                    </Typography>
                ) : null}
                <ButtonBase
                    onClick={onClose}
                    sx={{
                        mt: 2.25,
                        width: "100%",
                        borderRadius: 999,
                        p: "2.5px",
                        bgcolor: I.buttonRim,
                        boxShadow: "0 6px 10px rgba(15, 61, 54, 0.28)",
                        overflow: "hidden",
                        "@keyframes wwBtnShine": {
                            "0%": { transform: "translateX(-120px) skewX(-18deg)" },
                            "100%": { transform: "translateX(280px) skewX(-18deg)" },
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            minHeight: 52,
                            borderRadius: 999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: I.button,
                            border: "1px solid rgba(255,255,255,0.28)",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: 3,
                                left: 18,
                                right: 18,
                                height: 14,
                                borderRadius: 999,
                                bgcolor: "rgba(255,255,255,0.22)",
                            }}
                        />
                        <Box
                            aria-hidden
                            sx={{
                                position: "absolute",
                                top: 0,
                                bottom: 0,
                                width: 42,
                                bgcolor: "rgba(255,255,255,0.38)",
                                animation: "wwBtnShine 2.2s ease-in-out 0.4s infinite",
                            }}
                        />
                        <Typography
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                fontFamily: I.serif,
                                fontSize: 15,
                                fontWeight: 700,
                                letterSpacing: 1.1,
                                color: I.buttonText,
                                textShadow: "0 1px 2px rgba(0,40,35,0.35)",
                            }}
                        >
                            NEXT ➔
                        </Typography>
                    </Box>
                </ButtonBase>
            </Box>
        </Dialog>
    );
}
